/** Shared MCP types for tool definitions, results, and configuration. */

import type { PublicClient, TestClient, WalletClient } from "viem";
import type { z } from "zod";

// ---------------------------------------------------------------------------
// Tool results
// ---------------------------------------------------------------------------

export interface ToolResult {
  [key: string]: unknown;
  content: { type: "text"; text: string }[];
  isError?: boolean;
}

// ---------------------------------------------------------------------------
// Tool Context — injectable client access for handler extraction
// ---------------------------------------------------------------------------

/**
 * Context interface for extracted tool handlers.
 *
 * In the standard MCP server, this wraps the existing chain provider.
 * In webenv (browser), this wraps Tevm-backed clients.
 */
export interface ToolContext {
  /** Get a PublicClient for the given chain. */
  getClient(chainIdOrName?: string | number): PublicClient;
  /** Get a WalletClient for the given chain (optional — not all contexts support writes). */
  getWalletClient?(chainIdOrName?: string | number): WalletClient;
  /** Get a TestClient (optional — only available in dev environments like webenv/devenv). */
  getTestClient?(chainIdOrName?: string | number): TestClient;
}

// ---------------------------------------------------------------------------
// Tool definition
// ---------------------------------------------------------------------------

/** Generic tool definition for the MCP tool registry. */
export interface ToolDef<TParams = Record<string, unknown>> {
  name: string;
  description: string;
  category: string;
  inputSchema: Record<string, z.ZodTypeAny>;
  handler: (
    params: TParams,
    ctx: ToolContext,
  ) => ToolResult | Promise<ToolResult>;
  capabilities?: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toToolResult(data: unknown, isError?: boolean): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify(data, null, 2) }],
    ...(isError ? { isError: true } : {}),
  };
}

export function toErrorResult(error: {
  code: string;
  message: string;
  suggestion?: string;
}): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify(error, null, 2) }],
    isError: true,
  };
}

// ---------------------------------------------------------------------------
// WebMCP configuration
// ---------------------------------------------------------------------------

/** Tool profile controlling which tools are registered. */
export type WebMcpProfile =
  | "webenv-data"
  | "webenv-trader"
  | "webenv-lp"
  | "webenv-full"
  | "webenv-dev";

/** Activity log entry for tool calls. */
export interface WebMcpLogEntry {
  timestamp: number;
  clientId: string;
  toolName: string;
  statusCode: "success" | "error";
  latencyMs: number;
}

/** WebMCP server configuration. */
export interface WebMcpConfig {
  enabled: boolean;
  wsPort: number;
  profile: WebMcpProfile;
  maxClients: number;
  maxLogEntries: number;
  allowedOrigins: string[];
}

/** Default WebMCP configuration. */
export const DEFAULT_WEBMCP_CONFIG: WebMcpConfig = {
  enabled: false,
  wsPort: 3004,
  profile: "webenv-full",
  maxClients: 10,
  maxLogEntries: 1000,
  allowedOrigins: ["http://localhost:3003"],
};
